import { getHomeItemById } from 'reactApp/modules/home/home.selectors';
import { HomeItem } from 'reactApp/modules/home/home.types';
import { getHomeList } from 'reactApp/modules/home/selector/getHomeList';
import { getPublicItemById } from 'reactApp/modules/public/public.selectors';
import { getSearchItemById } from 'reactApp/modules/search/search.selectors';
import type { RootState } from 'reactApp/store';
import { createSelector } from 'reselect';

import type { DomainFolderItem, DomainFoldersState } from './domainFolders.types';

const getDomainFoldersState = (state: RootState): DomainFoldersState => state.domainsFolders;
const getId = (state, id) => id;

export const getDomainFolderById = createSelector(getDomainFoldersState, getId, (folders, folderId): DomainFolderItem | undefined => {
    const lowerFolderId = folderId?.toLowerCase();
    return folders.list[lowerFolderId];
});

export const getDomainFolderQuotaFree = createSelector(getDomainFoldersState, getId, (folders, folderId) => {
    const lowerFolderId = folderId.toLowerCase();
    if (folders.list[lowerFolderId]) {
        return folders.list[lowerFolderId].quota - folders.list[lowerFolderId].size;
    }

    return 0;
});

export const getClosestDomainFolder = createSelector(
    getDomainFoldersState,
    (_, folderId: string) => folderId,
    (folders, folderId) => {
        const pathParts = folderId?.toLowerCase().split('/').filter(Boolean);
        while (pathParts?.length) {
            const domainFolder = folders.list[`/${pathParts.join('/')}`];
            if (domainFolder) {
                return domainFolder;
            }
            pathParts.pop();
        }

        return null;
    }
);

export const getClosestDomainBitmask = createSelector(
    (_, folderId: string) => folderId,
    (state: RootState, folderId: string) => getPublicItemById(state, folderId),
    (state: RootState, folderId: string) => getHomeItemById(state, folderId),
    (state: RootState, folderId: string) => getSearchItemById(state, folderId),
    getHomeList,
    getDomainFoldersState,
    (folderId, publicItem, homeItem, searchItem, homeList, domainFolders) => {
        const findParent = (folderId: string | undefined, list: Record<string, HomeItem | DomainFolderItem>) => {
            const pathParts = folderId?.split('/').filter(Boolean);
            while (pathParts?.length) {
                const folder = list[`/${pathParts.join('/')}`] ?? list[`/${pathParts.join('/').toLowerCase()}`];
                if (typeof folder?.mode_bitmask === 'number') {
                    return folder.mode_bitmask;
                }
                pathParts.pop();
            }
            return null;
        };

        if (typeof publicItem?.mode_bitmask === 'number') {
            return publicItem.mode_bitmask;
        }
        if (typeof homeItem?.mode_bitmask === 'number') {
            return homeItem.mode_bitmask;
        }
        if (typeof searchItem?.mode_bitmask === 'number') {
            return searchItem.mode_bitmask;
        }

        const homeParent = findParent(folderId, homeList);
        const domainParent = findParent(folderId, domainFolders.list);

        return homeParent ?? domainParent;
    }
);
